import { encodeSitePublicRef } from '../utils/site-public-ref.util';

export type SitePublicBlockSection = 'overview' | 'hourly_rate' | 'monthly' | 'other' | string;
export type SitePublicBlockType = 'text' | 'image' | string;

/** การ์ดลานบน Home */
export interface HomeParkingSiteCard {
  publicRef: string;
  titleTh: string;
  titleEn?: string | null;
  summaryTh?: string | null;
  summaryEn?: string | null;
  coverImage?: string | null;
  isOnline: boolean;
  sortOrder: number;
  amenityCodes: string[];
}

export type SitePublicImageCategory = 'parking' | 'surrounding' | 'entrance' | 'facility' | string;

/** รูป gallery การตลาด — image เป็น URL CDN */
export interface SitePublicImage {
  category: SitePublicImageCategory;
  image: string;
  captionTh?: string | null;
  captionEn?: string | null;
  sortOrder: number;
}

/** ไอคอนความเชื่อมั่น (EV มาจากระบบ) */
export interface SitePublicAmenity {
  code: string;
  labelTh: string;
  labelEn: string;
  noteTh?: string | null;
  noteEn?: string | null;
  isSystem: boolean;
}

export interface SitePublicBlock {
  id: number;
  sortOrder: number;
  section: SitePublicBlockSection;
  blockType: SitePublicBlockType;
  titleTh?: string | null;
  titleEn?: string | null;
  bodyTh?: string | null;
  bodyEn?: string | null;
  image?: string | null;
  isActive: boolean;
}

/** รายละเอียดลานสาธารณะ (TH/EN + blocks) */
export interface SitePublicProfileDetail {
  publicRef?: string | null;
  siteName: string;
  siteIsOnline: boolean;
  titleTh: string;
  titleEn?: string | null;
  summaryTh?: string | null;
  summaryEn?: string | null;
  addressTh?: string | null;
  addressEn?: string | null;
  coverImage?: string | null;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blocks: SitePublicBlock[];
  images: SitePublicImage[];
  amenities: SitePublicAmenity[];
  siteHasEv: boolean;
}

function toCodeList(raw: any): string[] {
  return Array.isArray(raw) ? raw.map((c) => String(c).toLowerCase()).filter(Boolean) : [];
}

export function mapSitePublicImage(raw: any): SitePublicImage {
  return {
    category: String(raw?.category ?? raw?.Category ?? 'parking').toLowerCase(),
    image: String(raw?.image ?? raw?.Image ?? ''),
    captionTh: raw?.captionTh ?? raw?.CaptionTh ?? null,
    captionEn: raw?.captionEn ?? raw?.CaptionEn ?? null,
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
  };
}

export function mapSitePublicAmenity(raw: any): SitePublicAmenity {
  return {
    code: String(raw?.code ?? raw?.Code ?? '').toLowerCase(),
    labelTh: String(raw?.labelTh ?? raw?.LabelTh ?? ''),
    labelEn: String(raw?.labelEn ?? raw?.LabelEn ?? ''),
    noteTh: raw?.noteTh ?? raw?.NoteTh ?? null,
    noteEn: raw?.noteEn ?? raw?.NoteEn ?? null,
    isSystem: !!(raw?.isSystem ?? raw?.IsSystem),
  };
}

export function mapHomeParkingSiteCard(raw: any): HomeParkingSiteCard {
  // API ส่ง publicRef (encode จาก profile id) · fallback encode ฝั่ง FE ให้ตรง helper เดียวกัน
  let publicRef = String(raw?.publicRef ?? raw?.PublicRef ?? '').trim();
  if (!publicRef) {
    const profileId = Number(raw?.profileId ?? raw?.ProfileId ?? raw?.id ?? raw?.Id ?? 0);
    publicRef = encodeSitePublicRef(profileId);
  }

  return {
    publicRef,
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: raw?.titleEn ?? raw?.TitleEn ?? null,
    summaryTh: raw?.summaryTh ?? raw?.SummaryTh ?? null,
    summaryEn: raw?.summaryEn ?? raw?.SummaryEn ?? null,
    coverImage: raw?.coverImage ?? raw?.CoverImage ?? null,
    isOnline: !!(raw?.isOnline ?? raw?.IsOnline),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    amenityCodes: toCodeList(raw?.amenityCodes ?? raw?.AmenityCodes),
  };
}

export function mapSitePublicBlock(raw: any): SitePublicBlock {
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    section: String(raw?.section ?? raw?.Section ?? 'other').toLowerCase(),
    blockType: String(raw?.blockType ?? raw?.BlockType ?? 'text').toLowerCase(),
    titleTh: raw?.titleTh ?? raw?.TitleTh ?? null,
    titleEn: raw?.titleEn ?? raw?.TitleEn ?? null,
    bodyTh: raw?.bodyTh ?? raw?.BodyTh ?? null,
    bodyEn: raw?.bodyEn ?? raw?.BodyEn ?? null,
    image: raw?.image ?? raw?.Image ?? null,
    isActive: raw?.isActive ?? raw?.IsActive ?? true,
  };
}

export function mapSitePublicProfileDetail(raw: any): SitePublicProfileDetail {
  const blocks = raw?.blocks ?? raw?.Blocks ?? [];
  const images = raw?.images ?? raw?.Images ?? [];
  const amenities = raw?.amenities ?? raw?.Amenities ?? [];
  let publicRef = String(raw?.publicRef ?? raw?.PublicRef ?? '').trim();
  if (!publicRef) {
    const profileId = Number(raw?.id ?? raw?.Id ?? raw?.profileId ?? raw?.ProfileId ?? 0);
    publicRef = encodeSitePublicRef(profileId);
  }

  return {
    publicRef: publicRef || null,
    siteName: String(raw?.siteName ?? raw?.SiteName ?? ''),
    siteIsOnline: !!(raw?.siteIsOnline ?? raw?.SiteIsOnline),
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: raw?.titleEn ?? raw?.TitleEn ?? null,
    summaryTh: raw?.summaryTh ?? raw?.SummaryTh ?? null,
    summaryEn: raw?.summaryEn ?? raw?.SummaryEn ?? null,
    addressTh: raw?.addressTh ?? raw?.AddressTh ?? null,
    addressEn: raw?.addressEn ?? raw?.AddressEn ?? null,
    coverImage: raw?.coverImage ?? raw?.CoverImage ?? null,
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    isPublished: !!(raw?.isPublished ?? raw?.IsPublished),
    isActive: !!(raw?.isActive ?? raw?.IsActive),
    blocks: Array.isArray(blocks) ? blocks.map(mapSitePublicBlock) : [],
    images: Array.isArray(images)
      ? images.map(mapSitePublicImage).filter((img) => !!img.image).sort((a, b) => a.sortOrder - b.sortOrder)
      : [],
    amenities: Array.isArray(amenities) ? amenities.map(mapSitePublicAmenity).filter((a) => !!a.code) : [],
    siteHasEv: !!(raw?.siteHasEv ?? raw?.SiteHasEv),
  };
}
